import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Ho_Chi_Minh'; // UTC+7

/** Định dạng thời gian theo múi giờ Việt Nam (UTC+7).
 *  DB lưu UTC (Prisma serialize chuỗi ISO có hậu tố 'Z') — phải QUY ĐỔI sang giờ VN.
 *  Lưu ý: dùng dayjs(value).tz(TZ) chứ KHÔNG dùng dayjs.tz(value, TZ) — hàm sau bỏ qua
 *  offset 'Z' và in nguyên con số UTC, làm thời gian hiển thị chậm 7 tiếng so với thực tế. */
export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = dayjs(value).tz(TZ);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm:ss') : '—';
}

/** Thời lượng tạo: từ lúc bắt đầu (createdAt) đến lúc hoàn tất/lỗi (completedAt).
 *  <60s -> "30s"; >=60s -> "1p30s" (phút+giây). Chưa xong (thiếu completedAt) -> "—". */
export function formatDuration(start?: string | null, end?: string | null): string {
  if (!start || !end) return '—';
  const ms = dayjs(end).valueOf() - dayjs(start).valueOf();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return seconds ? `${minutes}p${seconds}s` : `${minutes}p`;
}

/** Chuyển object/giá trị thành chuỗi JSON đẹp để hiển thị / chỉnh sửa. */
export function toJsonText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') {
    // Nếu vốn đã là chuỗi JSON thì cố format lại.
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

export interface ParseResult {
  ok: boolean;
  value?: unknown;
  error?: string;
}

/** Phân tích chuỗi JSON; chuỗi rỗng -> undefined (hợp lệ). */
export function parseJsonText(text: string): ParseResult {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

/**
 * Gộp `webhookSecret` (ô nhập riêng) vào object config trước khi lưu.
 * - secret rỗng -> bỏ key khỏi config; config rỗng + secret rỗng -> giữ nguyên.
 * - config không phải object -> bọc thành object nếu có secret.
 */
export function mergeWebhookSecret(configValue: unknown, secret?: string): unknown {
  const base = configValue && typeof configValue === 'object' ? { ...(configValue as Record<string, unknown>) } : undefined;
  if (secret && secret.trim()) {
    return { ...(base || {}), webhookSecret: secret.trim() };
  }
  if (base) delete base.webhookSecret;
  return base ?? configValue;
}

/** 1 dòng cấu hình polling theo danh mục (đơn vị giây/phút cho form). */
export interface PollingRow {
  category?: string;
  pollInitialDelaySec?: number;
  pollIntervalSec?: number;
  pollMaxWaitMin?: number;
}

/** Lấy danh sách polling theo danh mục (provider.config.pollingByCategory) -> form rows. */
export function pollingListFromConfig(config: unknown): PollingRow[] {
  const c = (config && typeof config === 'object' ? config : {}) as Record<string, unknown>;
  const map = (c.pollingByCategory && typeof c.pollingByCategory === 'object'
    ? c.pollingByCategory
    : {}) as Record<string, Record<string, unknown>>;
  const ms2s = (v: unknown) => (typeof v === 'number' ? v / 1000 : undefined);
  const ms2min = (v: unknown) => (typeof v === 'number' ? v / 60000 : undefined);
  return Object.entries(map).map(([category, e]) => ({
    category,
    pollInitialDelaySec: ms2s(e?.pollInitialDelayMs),
    pollIntervalSec: ms2s(e?.pollIntervalMs),
    pollMaxWaitMin: ms2min(e?.pollMaxWaitMs),
  }));
}

/** Bỏ các key được quản lý bằng ô riêng (webhookSecret + pollingByCategory + rateLimitPerMin + maxConcurrent) khỏi config JSON. */
export function stripManagedConfig(config: unknown): unknown {
  if (!config || typeof config !== 'object') return config;
  const c = { ...(config as Record<string, unknown>) };
  delete c.webhookSecret;
  delete c.pollingByCategory;
  delete c.rateLimitPerMin;
  delete c.maxConcurrent;
  return c;
}

/** Đọc giới hạn request/phút từ config (ô nhập riêng). undefined nếu không cấu hình. */
export function rateLimitFromConfig(config: unknown): number | undefined {
  const c = (config && typeof config === 'object' ? config : {}) as Record<string, unknown>;
  const v = Number(c.rateLimitPerMin);
  return Number.isFinite(v) ? v : undefined;
}

/** Đọc số request đồng thời tối đa từ config (ô nhập riêng). undefined nếu không cấu hình. */
export function maxConcurrentFromConfig(config: unknown): number | undefined {
  const c = (config && typeof config === 'object' ? config : {}) as Record<string, unknown>;
  const v = Number(c.maxConcurrent);
  return Number.isFinite(v) ? v : undefined;
}

/**
 * Gộp `maxConcurrent` (ô nhập riêng) vào config trước khi lưu.
 * - >0  -> đặt giới hạn số request đồng thời.
 * - 0   -> đặt 0 (bỏ giới hạn TƯỜNG MINH, ghi đè mặc định seed).
 * - rỗng/undefined -> bỏ key khỏi config.
 */
export function mergeMaxConcurrent(configValue: unknown, maxConcurrent?: number | null): unknown {
  const base = configValue && typeof configValue === 'object' ? { ...(configValue as Record<string, unknown>) } : undefined;
  if (maxConcurrent != null && Number.isFinite(Number(maxConcurrent))) {
    return { ...(base || {}), maxConcurrent: Math.max(0, Math.floor(Number(maxConcurrent))) };
  }
  if (base) delete base.maxConcurrent;
  return base ?? configValue;
}

/**
 * Gộp `rateLimitPerMin` (ô nhập riêng) vào config trước khi lưu.
 * - >0  -> đặt giới hạn.
 * - 0   -> đặt 0 (bỏ giới hạn TƯỜNG MINH, ghi đè mặc định seed).
 * - rỗng/undefined -> bỏ key khỏi config.
 */
export function mergeRateLimit(configValue: unknown, rateLimitPerMin?: number | null): unknown {
  const base = configValue && typeof configValue === 'object' ? { ...(configValue as Record<string, unknown>) } : undefined;
  if (rateLimitPerMin != null && Number.isFinite(Number(rateLimitPerMin))) {
    return { ...(base || {}), rateLimitPerMin: Math.max(0, Math.floor(Number(rateLimitPerMin))) };
  }
  if (base) delete base.rateLimitPerMin;
  return base ?? configValue;
}

/** Gộp danh sách polling theo danh mục (form rows) vào config.pollingByCategory (ms). */
export function mergePollingByCategory(config: unknown, rows: PollingRow[] | undefined): unknown {
  const base = (config && typeof config === 'object' ? { ...(config as Record<string, unknown>) } : {}) as Record<
    string,
    unknown
  >;
  const map: Record<string, Record<string, number>> = {};
  for (const r of rows || []) {
    const cat = (r?.category || '').trim();
    if (!cat) continue;
    const entry: Record<string, number> = {};
    if (r.pollInitialDelaySec != null) entry.pollInitialDelayMs = Math.round(Number(r.pollInitialDelaySec) * 1000);
    if (r.pollIntervalSec != null) entry.pollIntervalMs = Math.round(Number(r.pollIntervalSec) * 1000);
    if (r.pollMaxWaitMin != null) entry.pollMaxWaitMs = Math.round(Number(r.pollMaxWaitMin) * 60000);
    if (Object.keys(entry).length) map[cat] = entry;
  }
  if (Object.keys(map).length) base.pollingByCategory = map;
  else delete base.pollingByCategory;
  return Object.keys(base).length ? base : undefined;
}

/** Validator cho antd Form đối với ô nhập JSON (cho phép rỗng). */
export function jsonValidator(_rule: unknown, value: string) {
  if (!value || !value.trim()) return Promise.resolve();
  try {
    JSON.parse(value);
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error('JSON không hợp lệ'));
  }
}

const PARAM_TYPES = [
  'textarea', 'text', 'number', 'select', 'bool', 'json', 'color',
  'image', 'video', 'audio', 'media', 'image[]', 'video[]',
];

/** Validator cho ô nhập paramsSchema: rỗng OK; nếu có phải là mảng {name,label,type∈ParamType}. */
export function paramsSchemaValidator(_rule: unknown, value: string) {
  if (!value || !value.trim()) return Promise.resolve();
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return Promise.reject(new Error('JSON không hợp lệ'));
  }
  if (!Array.isArray(parsed)) return Promise.reject(new Error('paramsSchema phải là một mảng (array)'));
  for (let i = 0; i < parsed.length; i++) {
    const f = parsed[i] as Record<string, unknown>;
    if (!f || typeof f !== 'object') return Promise.reject(new Error(`Phần tử #${i + 1} không phải object`));
    if (typeof f.name !== 'string' || !f.name) return Promise.reject(new Error(`Phần tử #${i + 1} thiếu "name"`));
    if (typeof f.label !== 'string' || !f.label) return Promise.reject(new Error(`Phần tử #${i + 1} thiếu "label"`));
    if (typeof f.type !== 'string' || !PARAM_TYPES.includes(f.type))
      return Promise.reject(new Error(`Phần tử #${i + 1} có "type" không hợp lệ (cho phép: ${PARAM_TYPES.join(', ')})`));
  }
  return Promise.resolve();
}
